import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import Navbar from "../components/Navbar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Settings, Save, RefreshCw, Wifi, CheckCircle, XCircle, ExternalLink, 
  Mail, Video, AlertCircle 
} from "lucide-react";

export default function ExternalConnections({ user, logout, previewRole, setPreviewRole, previewDealership, setPreviewDealership, previewDepartment, setPreviewDepartment }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("email");
  
  // Use preview role if admin is previewing, otherwise use actual role
  const effectiveRole = previewRole || user.role;
  
  // Check if user can edit (only Super Admin can edit)
  const canEdit = effectiveRole === "super_admin";

  // Email Connection State
  const [emailConnection, setEmailConnection] = useState({
    enabled: false,
    tenant_id: "",
    client_id: "",
    client_secret: "",
    sender_email: "",
    connected: false,
    last_test: null
  });
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);

  // Microsoft 365 Status Connection State
  const [microsoft365Connection, setMicrosoft365Connection] = useState({
    enabled: false,
    tenant_id: "",
    client_id: "",
    client_secret: "",
    connected: false,
    last_test: null
  });
  const [savingMicrosoft365, setSavingMicrosoft365] = useState(false);
  const [testingMicrosoft365, setTestingMicrosoft365] = useState(false);
  const [microsoft365TestResult, setMicrosoft365TestResult] = useState(null);

  // Webex Status Connection State
  const [webexConnection, setWebexConnection] = useState({
    enabled: false,
    access_token: "",
    connected: false,
    last_test: null
  });
  const [savingWebex, setSavingWebex] = useState(false);
  const [testingWebex, setTestingWebex] = useState(false);
  const [webexTestResult, setWebexTestResult] = useState(null);

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/external-connections`);
      const data = response.data;
      
      if (data.email) {
        setEmailConnection(data.email);
      }
      if (data.microsoft365) {
        setMicrosoft365Connection(data.microsoft365);
      }
      if (data.webex) {
        setWebexConnection(data.webex);
      }
    } catch (error) {
      console.error("Error fetching connections:", error);
    } finally {
      setLoading(false);
    }
  };

  // Email Connection Handlers
  const handleSaveEmail = async () => {
    try {
      setSavingEmail(true);
      setEmailTestResult(null);
      await axios.patch(`${API}/admin/external-connections/email`, {
        enabled: emailConnection.enabled,
        tenant_id: emailConnection.tenant_id,
        client_id: emailConnection.client_id,
        client_secret: emailConnection.client_secret || undefined,
        sender_email: emailConnection.sender_email
      });
      toast.success("Email connection settings saved");
      fetchConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save email settings");
    } finally {
      setSavingEmail(false);
    }
  };

  const handleTestEmail = async () => {
    try {
      setTestingEmail(true);
      setEmailTestResult(null);
      const response = await axios.post(`${API}/admin/external-connections/email/test`);
      setEmailTestResult(response.data);
      if (response.data.success) {
        toast.success("Email connection test successful!");
        fetchConnections();
      } else {
        toast.error(response.data.error || "Email connection test failed");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Email connection test failed";
      setEmailTestResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setTestingEmail(false);
    }
  };

  // Microsoft 365 Status Connection Handlers
  const handleSaveMicrosoft365 = async () => {
    try {
      setSavingMicrosoft365(true);
      setMicrosoft365TestResult(null);
      await axios.patch(`${API}/admin/external-connections/microsoft365`, {
        enabled: microsoft365Connection.enabled,
        tenant_id: microsoft365Connection.tenant_id,
        client_id: microsoft365Connection.client_id,
        client_secret: microsoft365Connection.client_secret || undefined
      });
      toast.success("Microsoft 365 connection settings saved");
      fetchConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save Microsoft 365 settings");
    } finally {
      setSavingMicrosoft365(false);
    }
  };

  const handleTestMicrosoft365 = async () => {
    try {
      setTestingMicrosoft365(true);
      setMicrosoft365TestResult(null);
      const response = await axios.post(`${API}/admin/external-connections/microsoft365/test`);
      setMicrosoft365TestResult(response.data);
      if (response.data.success) {
        toast.success("Microsoft 365 connection test successful!");
        fetchConnections();
      } else {
        toast.error(response.data.error || "Microsoft 365 connection test failed");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Microsoft 365 connection test failed";
      setMicrosoft365TestResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setTestingMicrosoft365(false);
    }
  };

  // Webex Status Connection Handlers
  const handleSaveWebex = async () => {
    try {
      setSavingWebex(true);
      setWebexTestResult(null);
      await axios.patch(`${API}/admin/external-connections/webex`, {
        enabled: webexConnection.enabled,
        access_token: webexConnection.access_token || undefined
      });
      toast.success("Webex connection settings saved");
      fetchConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save Webex settings");
    } finally {
      setSavingWebex(false);
    }
  };

  const handleTestWebex = async () => {
    try {
      setTestingWebex(true);
      setWebexTestResult(null);
      const response = await axios.post(`${API}/admin/external-connections/webex/test`);
      setWebexTestResult(response.data);
      if (response.data.success) {
        toast.success("Webex connection test successful!");
        fetchConnections();
      } else {
        toast.error(response.data.error || "Webex connection test failed");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Webex connection test failed";
      setWebexTestResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setTestingWebex(false);
    }
  };

  const ConnectionStatusBadge = ({ connected, enabled }) => {
    if (!enabled) {
      return (
        <span className="flex items-center gap-1 text-sm text-slate-400">
          <XCircle className="w-4 h-4" />
          Disabled
        </span>
      );
    }
    if (connected) {
      return (
        <span className="flex items-center gap-1 text-sm text-green-600">
          <CheckCircle className="w-4 h-4" />
          Connected
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 text-sm text-yellow-600">
        <AlertCircle className="w-4 h-4" />
        Not Verified
      </span>
    );
  };

  const TestResultAlert = ({ result }) => {
    if (!result) return null;
    
    return (
      <div className={`p-3 rounded-lg ${result.success ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
        <div className="flex items-start gap-2">
          {result.success ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          ) : (
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          )}
          <p className={`text-sm ${result.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
            {result.success ? result.message : result.error}
          </p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-[#1E1E1E] dark:to-[#252525]">
        <Navbar 
          user={user} 
          logout={logout} 
          effectiveRole={effectiveRole}
          previewRole={previewRole} 
          setPreviewRole={setPreviewRole}
          previewDealership={previewDealership}
          setPreviewDealership={setPreviewDealership}
        />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-6 h-6 animate-spin mr-2" />
            <span className="text-lg text-slate-600 dark:text-white">Loading connections...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-[#1E1E1E] dark:to-[#252525]">
      <Navbar 
        user={user} 
        logout={logout} 
        effectiveRole={effectiveRole}
        previewRole={previewRole} 
        setPreviewRole={setPreviewRole}
        previewDealership={previewDealership}
        setPreviewDealership={setPreviewDealership}
      />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Wifi className="w-8 h-8 text-[#7a1d42]" />
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">External Connections</h1>
          </div>
          <p className="text-slate-600 dark:text-white">Configure API connections for email and service status monitoring</p>
        </div>

        {/* View Only Notice for Admins */}
        {!canEdit && (
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>View Only:</strong> You have read-only access to this page. Contact a Super Admin to make changes.
              </p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </TabsTrigger>
            <TabsTrigger value="microsoft365" className="flex items-center gap-2">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.5 3v8.5H3V3h8.5zm0 18H3v-8.5h8.5V21zm1-18H21v8.5h-8.5V3zm8.5 9.5V21h-8.5v-8.5H21z"/>
              </svg>
              Microsoft 365
            </TabsTrigger>
            <TabsTrigger value="webex" className="flex items-center gap-2">
              <Video className="w-4 h-4" />
              Webex
            </TabsTrigger>
          </TabsList>

          {/* Email Tab */}
          <TabsContent value="email">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <Mail className="w-7 h-7 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle>Email (Microsoft Graph)</CardTitle>
                      <CardDescription>
                        Send emails via Microsoft 365 / Exchange Online
                      </CardDescription>
                    </div>
                  </div>
                  <ConnectionStatusBadge connected={emailConnection.connected} enabled={emailConnection.enabled} />
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable Toggle */}
                <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50 dark:bg-[#343434]">
                  <div className="space-y-1">
                    <Label htmlFor="email-enabled" className="text-base font-medium">
                      Enable Email Integration
                    </Label>
                    <p className="text-sm text-slate-500 dark:text-white">
                      Send notification emails through Microsoft Graph API
                    </p>
                  </div>
                  <Switch
                    id="email-enabled"
                    checked={emailConnection.enabled}
                    onCheckedChange={(checked) => setEmailConnection({
                      ...emailConnection,
                      enabled: checked
                    })}
                    disabled={!canEdit}
                  />
                </div>

                {/* Credentials Form */}
                {emailConnection.enabled && (
                  <div className="space-y-4 p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <h5 className="font-medium text-slate-900 dark:text-white">API Credentials</h5>
                      <a
                        href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Azure Portal
                      </a>
                    </div>

                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email-tenant-id">Tenant ID</Label>
                        <Input
                          id="email-tenant-id"
                          value={emailConnection.tenant_id || ""}
                          onChange={(e) => setEmailConnection({
                            ...emailConnection,
                            tenant_id: e.target.value
                          })}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          className="font-mono text-sm"
                          readOnly={!canEdit}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email-client-id">Client ID (Application ID)</Label>
                        <Input
                          id="email-client-id"
                          value={emailConnection.client_id || ""}
                          onChange={(e) => setEmailConnection({
                            ...emailConnection,
                            client_id: e.target.value
                          })}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          className="font-mono text-sm"
                          readOnly={!canEdit}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email-client-secret">Client Secret</Label>
                        <Input
                          id="email-client-secret"
                          type="password"
                          value={emailConnection.client_secret || ""}
                          onChange={(e) => setEmailConnection({
                            ...emailConnection,
                            client_secret: e.target.value
                          })}
                          placeholder={emailConnection.client_secret_masked || "Enter client secret..."}
                          className="font-mono text-sm"
                          readOnly={!canEdit}
                        />
                        {emailConnection.client_secret_masked && !emailConnection.client_secret && (
                          <p className="text-xs text-slate-500 dark:text-white">
                            Current: {emailConnection.client_secret_masked}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email-sender">Sender Email Address</Label>
                        <Input
                          id="email-sender"
                          type="email"
                          value={emailConnection.sender_email || ""}
                          onChange={(e) => setEmailConnection({
                            ...emailConnection,
                            sender_email: e.target.value
                          })}
                          placeholder="helpdesk@yourdomain.com"
                          className="text-sm"
                          readOnly={!canEdit}
                        />
                        <p className="text-xs text-slate-500 dark:text-white">
                          Must be a valid mailbox in your Microsoft 365 tenant
                        </p>
                      </div>
                    </div>

                    <TestResultAlert result={emailTestResult} />

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2">
                      {canEdit && (
                        <Button
                          onClick={handleSaveEmail}
                          disabled={savingEmail}
                          className="bg-[#7a1d42] hover:bg-[#5a1531] text-white"
                        >
                          {savingEmail ? (
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Save Credentials
                        </Button>
                      )}
                      <Button
                        onClick={handleTestEmail}
                        disabled={testingEmail || !emailConnection.tenant_id || !emailConnection.client_id || !emailConnection.sender_email}
                        variant="outline"
                      >
                        {testingEmail ? (
                          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Mail className="w-4 h-4 mr-2" />
                        )}
                        Test Connection
                      </Button>
                    </div>
                  </div>
                )}

                {/* Setup Instructions */}
                {emailConnection.enabled && !emailConnection.connected && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <h5 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Required Permissions</h5>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                      Your Azure AD app needs the following Microsoft Graph <strong>Application</strong> permission:
                    </p>
                    <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside">
                      <li><code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">Mail.Send</code> - Send mail as any user</li>
                    </ul>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                      Don't forget to grant admin consent after adding the permission.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Microsoft 365 Status Tab */}
          <TabsContent value="microsoft365">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                      <svg className="w-7 h-7 text-orange-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M11.5 3v8.5H3V3h8.5zm0 18H3v-8.5h8.5V21zm1-18H21v8.5h-8.5V3zm8.5 9.5V21h-8.5v-8.5H21z"/>
                      </svg>
                    </div>
                    <div>
                      <CardTitle>Microsoft 365 Service Health</CardTitle>
                      <CardDescription>
                        Monitor Exchange, Teams, SharePoint, OneDrive status
                      </CardDescription>
                    </div>
                  </div>
                  <ConnectionStatusBadge connected={microsoft365Connection.connected} enabled={microsoft365Connection.enabled} />
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable Toggle */}
                <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50 dark:bg-[#343434]">
                  <div className="space-y-1">
                    <Label htmlFor="ms365-enabled" className="text-base font-medium">
                      Enable Microsoft 365 Status Monitoring
                    </Label>
                    <p className="text-sm text-slate-500 dark:text-white">
                      Get real-time service health from Microsoft Graph API
                    </p>
                  </div>
                  <Switch
                    id="ms365-enabled"
                    checked={microsoft365Connection.enabled}
                    onCheckedChange={(checked) => setMicrosoft365Connection({
                      ...microsoft365Connection,
                      enabled: checked
                    })}
                    disabled={!canEdit}
                  />
                </div>

                {/* Credentials Form */}
                {microsoft365Connection.enabled && (
                  <div className="space-y-4 p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <h5 className="font-medium text-slate-900 dark:text-white">API Credentials</h5>
                      <a
                        href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Azure Portal
                      </a>
                    </div>

                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="ms365-tenant-id">Tenant ID</Label>
                        <Input
                          id="ms365-tenant-id"
                          value={microsoft365Connection.tenant_id || ""}
                          onChange={(e) => setMicrosoft365Connection({
                            ...microsoft365Connection,
                            tenant_id: e.target.value
                          })}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          className="font-mono text-sm"
                          readOnly={!canEdit}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="ms365-client-id">Client ID (Application ID)</Label>
                        <Input
                          id="ms365-client-id"
                          value={microsoft365Connection.client_id || ""}
                          onChange={(e) => setMicrosoft365Connection({
                            ...microsoft365Connection,
                            client_id: e.target.value
                          })}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          className="font-mono text-sm"
                          readOnly={!canEdit}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="ms365-client-secret">Client Secret</Label>
                        <Input
                          id="ms365-client-secret"
                          type="password"
                          value={microsoft365Connection.client_secret || ""}
                          onChange={(e) => setMicrosoft365Connection({
                            ...microsoft365Connection,
                            client_secret: e.target.value
                          })}
                          placeholder={microsoft365Connection.client_secret_masked || "Enter client secret..."}
                          readOnly={!canEdit}
                          className="font-mono text-sm"
                        />
                        {microsoft365Connection.client_secret_masked && !microsoft365Connection.client_secret && (
                          <p className="text-xs text-slate-500 dark:text-white">
                            Current: {microsoft365Connection.client_secret_masked}
                          </p>
                        )}
                      </div>
                    </div>

                    <TestResultAlert result={microsoft365TestResult} />

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2">
                      {canEdit && (
                        <Button
                          onClick={handleSaveMicrosoft365}
                          disabled={savingMicrosoft365}
                          className="bg-[#7a1d42] hover:bg-[#5a1531] text-white"
                        >
                          {savingMicrosoft365 ? (
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Save Credentials
                        </Button>
                      )}
                      <Button
                        onClick={handleTestMicrosoft365}
                        disabled={testingMicrosoft365 || !microsoft365Connection.tenant_id || !microsoft365Connection.client_id}
                        variant="outline"
                      >
                        {testingMicrosoft365 ? (
                          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Wifi className="w-4 h-4 mr-2" />
                        )}
                        Test Connection
                      </Button>
                    </div>
                  </div>
                )}

                {/* Setup Instructions */}
                {microsoft365Connection.enabled && !microsoft365Connection.connected && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <h5 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Required Permissions</h5>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                      Your Azure AD app needs the following Microsoft Graph <strong>Application</strong> permission:
                    </p>
                    <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside">
                      <li><code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">ServiceHealth.Read.All</code> - Read service health</li>
                    </ul>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                      Don't forget to grant admin consent after adding the permission.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Webex Status Tab */}
          <TabsContent value="webex">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                      <Video className="w-7 h-7 text-green-600" />
                    </div>
                    <div>
                      <CardTitle>Webex Service Status</CardTitle>
                      <CardDescription>
                        Monitor Webex Meetings, Messaging, and Calling status
                      </CardDescription>
                    </div>
                  </div>
                  <ConnectionStatusBadge connected={webexConnection.connected} enabled={webexConnection.enabled} />
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable Toggle */}
                <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50 dark:bg-[#343434]">
                  <div className="space-y-1">
                    <Label htmlFor="webex-enabled" className="text-base font-medium">
                      Enable Webex Status Monitoring
                    </Label>
                    <p className="text-sm text-slate-500 dark:text-white">
                      Get real-time Webex service status
                    </p>
                  </div>
                  <Switch
                    id="webex-enabled"
                    checked={webexConnection.enabled}
                    onCheckedChange={(checked) => setWebexConnection({
                      ...webexConnection,
                      enabled: checked
                    })}
                    disabled={!canEdit}
                  />
                </div>

                {/* Credentials Form */}
                {webexConnection.enabled && (
                  <div className="space-y-4 p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <h5 className="font-medium text-slate-900 dark:text-white">API Credentials</h5>
                      <a
                        href="https://developer.webex.com/my-apps"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Webex Developer Portal
                      </a>
                    </div>

                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="webex-token">Access Token</Label>
                        <Input
                          id="webex-token"
                          type="password"
                          value={webexConnection.access_token || ""}
                          onChange={(e) => setWebexConnection({
                            ...webexConnection,
                            access_token: e.target.value
                          })}
                          placeholder={webexConnection.access_token_masked || "Enter Webex access token..."}
                          className="font-mono text-sm"
                          readOnly={!canEdit}
                        />
                        {webexConnection.access_token_masked && !webexConnection.access_token && (
                          <p className="text-xs text-slate-500 dark:text-white">
                            Current: {webexConnection.access_token_masked}
                          </p>
                        )}
                        <p className="text-xs text-slate-500 dark:text-white">
                          You can get a personal access token from the Webex Developer Portal
                        </p>
                      </div>
                    </div>

                    <TestResultAlert result={webexTestResult} />

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2">
                      {canEdit && (
                        <Button
                          onClick={handleSaveWebex}
                          disabled={savingWebex}
                          className="bg-[#7a1d42] hover:bg-[#5a1531] text-white"
                        >
                          {savingWebex ? (
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Save Credentials
                        </Button>
                      )}
                      <Button
                        onClick={handleTestWebex}
                        disabled={testingWebex}
                        variant="outline"
                      >
                        {testingWebex ? (
                          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Video className="w-4 h-4 mr-2" />
                        )}
                        Test Connection
                      </Button>
                    </div>
                  </div>
                )}

                {/* Setup Instructions */}
                {webexConnection.enabled && !webexConnection.connected && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <h5 className="font-medium text-blue-800 dark:text-blue-200 mb-2">How to Get an Access Token</h5>
                    <ol className="text-sm text-blue-700 dark:text-blue-300 space-y-1 list-decimal list-inside">
                      <li>Go to <a href="https://developer.webex.com" target="_blank" rel="noopener noreferrer" className="underline">developer.webex.com</a></li>
                      <li>Sign in with your Webex account</li>
                      <li>Click on your avatar → "My Webex Apps"</li>
                      <li>Create a new app or use a Personal Access Token</li>
                      <li>Copy the access token and paste it above</li>
                    </ol>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                      <strong>Note:</strong> Personal access tokens expire after 12 hours. For production use, create a Service App or Integration.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
